// The first screen: dusk above the clouds, and GG ☸ BHARAT standing among them, the Konark wheel turning between the
// two words, "Bharat" flipping through India's scripts underneath. Scrolling flies you forward through the clouds and
// past the letters, then tips you down through the cloud floor towards India (the next section). The mouse leans the
// camera. Without WebGL2 (or under "reduce motion") the same sky is drawn with CSS and holds still.
import { useEffect, useRef, useState } from 'react';
import { REG } from '../brand';
import { openRegister } from '../Register';
import { REDUCE, ease, span, useScene } from './scenes';
import { Sky, toView, type Cam } from './sky';
import { Wheel } from './Wheel';

/** where the wordmark stands, in the sky's world (the camera starts at 0) */
const MARK_Z = -32;

const SCRIPTS: [string, string, string][] = [
  ['hi', 'भारत', 'Hindi'],
  ['pa', 'ਭਾਰਤ', 'Punjabi'],
  ['bn', 'ভারত', 'Bangla'],
  ['gu', 'ભારત', 'Gujarati'],
  ['kn', 'ಭಾರತ', 'Kannada'],
  ['or', 'ଭାରତ', 'Odia'],
  ['te', 'భారత్', 'Telugu'],
  ['ta', 'பாரதம்', 'Tamil'],
  ['ml', 'ഭാരതം', 'Malayalam'],
  ['ur', 'بھارت', 'Urdu'],
];
// just the letters these words need, from each script's Noto font
const SCRIPT_FONTS = `https://fonts.googleapis.com/css2?${['Noto+Sans+Gurmukhi', 'Noto+Sans+Bengali', 'Noto+Sans+Gujarati', 'Noto+Sans+Kannada', 'Noto+Sans+Oriya', 'Noto+Sans+Telugu', 'Noto+Sans+Tamil', 'Noto+Sans+Malayalam', 'Noto+Nastaliq+Urdu']
  .map((f) => `family=${f}:wght@700`)
  .join('&')}&display=swap&text=${encodeURIComponent(SCRIPTS.map((s) => s[1]).join(''))}`;

/** "Bharat" in one script after another, flipping like a departures board */
function Scripts() {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (!document.getElementById('gg-script-fonts')) {
      const l = document.createElement('link');
      l.id = 'gg-script-fonts';
      l.rel = 'stylesheet';
      l.href = SCRIPT_FONTS;
      document.head.appendChild(l);
    }
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const t = setInterval(() => setI((n) => (n + 1) % SCRIPTS.length), 2000);
    return () => clearInterval(t);
  }, []);
  const prev = (i + SCRIPTS.length - 1) % SCRIPTS.length;
  return (
    <p className="sk-scripts" aria-hidden>
      {SCRIPTS.map(([lang, w], k) => (
        <span key={lang} lang={lang} dir={lang === 'ur' ? 'rtl' : undefined} className={k === i ? 'on' : k === prev ? 'off' : ''}>
          {w}
        </span>
      ))}
      <small key={i} className="mono">
        {SCRIPTS[i][2]}
      </small>
    </p>
  );
}

/** a paper kite (patang) with its tail, bobbing on the wind */
function Kite({ hue, className }: { hue: string; className: string }) {
  return (
    <svg className={`sk-kite ${className}`} viewBox="-30 -40 60 140" aria-hidden style={{ '--kh': hue } as React.CSSProperties}>
      <path className="string" d="M0 26 C 10 60, -8 90, 6 140" />
      <path d="M0 -36 L26 0 L0 26 L-26 0 Z" fill="var(--kh)" />
      <path d="M0 -36 L0 26 M-26 0 Q0 -8 26 0" stroke="rgba(40,12,20,.45)" strokeWidth="1.4" fill="none" />
      <path d="M0 -36 L26 0 L0 0 Z" fill="rgba(255,255,255,.18)" />
      <path className="tail" d="M0 26 l-7 10 l7 4 l7 -4 Z" fill="var(--kh)" />
    </svg>
  );
}

export function Hero({ onDescend }: { onDescend: () => void }) {
  const sec = useRef<HTMLElement>(null);
  const stage = useRef<HTMLDivElement>(null);
  const backC = useRef<HTMLCanvasElement>(null);
  const frontC = useRef<HTMLCanvasElement>(null);
  const mark = useRef<HTMLDivElement>(null);
  const copy = useRef<HTMLDivElement>(null);
  const kites = useRef<HTMLDivElement>(null);
  const prog = useRef(0);
  const wake = useRef<() => void>(() => {});
  const [gl, setGl] = useState<'wait' | 'on' | 'off'>('wait');

  useScene(sec, (p) => {
    // still, the sky holds its first view
    prog.current = REDUCE ? 0 : p;
    wake.current();
  });

  useEffect(() => {
    const st = stage.current!;
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const small = matchMedia('(max-width: 760px)').matches || matchMedia('(pointer: coarse)').matches;
    let sky: Sky | null = null;
    try {
      sky = new Sky(backC.current!, frontC.current!, MARK_Z, small ? 320 : 720);
      setGl('on');
    } catch {
      setGl('off');
    }
    const cam: Cam = sky?.cam ?? { x: 0, y: 0, z: 0, pitch: 0, yaw: 0, fov: 1 };
    let vw = 1;
    let vh = 1;
    const scale = Math.min(devicePixelRatio || 1, 2) * (small ? 0.5 : 0.6);
    const size = () => {
      const r = st.getBoundingClientRect();
      vw = r.width;
      vh = r.height;
      sky?.resize(vw, vh, scale);
      wake.current();
    };
    const ro = new ResizeObserver(size);
    ro.observe(st);
    size();
    // the mouse leans the camera a little
    let mx = 0;
    let my = 0;
    let lx = 0;
    let ly = 0;
    const onMove = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      mx = (e.clientX / innerWidth) * 2 - 1;
      my = (e.clientY / innerHeight) * 2 - 1;
      wake.current();
    };
    addEventListener('pointermove', onMove, { passive: true });
    let visible = true;
    const io = new IntersectionObserver(([e]) => {
      visible = e.isIntersecting;
      wake.current();
    });
    io.observe(sec.current!);
    const t0 = performance.now();
    let raf = 0;
    const loop = (now: number) => {
      raf = 0;
      const t = (now - t0) / 1000;
      const intro = reduce ? 1 : ease.out(Math.min(1, t / 2.8));
      lx += (mx - lx) * 0.04;
      ly += (my - ly) * 0.04;
      const p = prog.current;
      const fly = ease.sine(span(p, 0, 0.82));
      const dive = ease.inOut(span(p, 0.42, 1));
      cam.fov = ((vw < vh ? 70 : 55) * Math.PI) / 180;
      cam.x = lx * 2.4 * (1 - dive);
      cam.y = -ly * 1.2 * (1 - dive) - ease.in(span(p, 0.5, 1)) * 15;
      cam.z = (1 - intro) * 14 - fly * 108;
      cam.pitch = -ly * 0.035 * (1 - dive) - dive * 1.22;
      cam.yaw = -lx * 0.05 * (1 - dive);
      sky?.render(reduce ? 0 : t);
      // the wordmark, as if it stood among the clouds
      const f = vh / 2 / Math.tan(cam.fov / 2);
      const v = toView(cam, 0, 0, MARK_Z);
      if (mark.current) {
        const s = v.depth > 0.6 ? -MARK_Z / v.depth : 0;
        const o = v.depth > 0.6 ? 1 - span(s, 1.55, 3.1) : 0;
        mark.current.style.transform = `translate3d(${((v.x * f) / Math.max(v.depth, 0.6)).toFixed(1)}px, ${((-v.y * f) / Math.max(v.depth, 0.6)).toFixed(1)}px, 0) scale(${s.toFixed(4)})`;
        mark.current.style.opacity = o.toFixed(3);
        mark.current.style.setProperty('--turn', `${(p * 540 + t * 6).toFixed(1)}deg`);
      }
      if (copy.current) {
        const k = span(p, 0, 0.09);
        copy.current.style.opacity = (1 - k).toFixed(3);
        copy.current.style.transform = `translate3d(0, ${(-k * 60).toFixed(1)}px, 0)`;
        copy.current.style.visibility = k >= 1 ? 'hidden' : '';
      }
      if (kites.current)
        for (const [i, el] of Array.from(kites.current.children as HTMLCollectionOf<HTMLElement>).entries()) {
          const [kx, ky, kz] = i === 0 ? [-19, 7.5, -70] : [24, 10, -95];
          const k = toView(cam, kx + Math.sin(t * 0.6 + i) * 0.8, ky + Math.sin(t * 0.9 + i * 2) * 0.5, kz);
          const ks = k.depth > 1 ? 14 / k.depth : 0;
          el.style.transform = `translate3d(${((k.x * f) / Math.max(k.depth, 1)).toFixed(1)}px, ${((-k.y * f) / Math.max(k.depth, 1)).toFixed(1)}px, 0) scale(${ks.toFixed(3)}) rotate(${(Math.sin(t * 0.8 + i) * 7).toFixed(1)}deg)`;
          el.style.opacity = (1 - span(p, 0.35, 0.6)).toFixed(3);
        }
      st.style.setProperty('--dive', dive.toFixed(3));
      st.style.setProperty('--veil', span(p, 0.74, 1).toFixed(3));
      // the horizon rises as the camera tips down (and dips a little when the mouse leans it up)
      st.style.setProperty('--hz', (f * Math.tan(Math.max(-1.35, Math.min(0.5, -cam.pitch)))).toFixed(1));
      // keep drawing while it's on screen and moving (WebGL clouds always drift); otherwise wait to be woken
      const moving = !reduce && (!!sky || intro < 1 || Math.abs(mx - lx) + Math.abs(my - ly) > 0.002);
      if (visible && moving && p < 1) raf = requestAnimationFrame(loop);
    };
    wake.current = () => {
      if (!raf && visible) raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      wake.current = () => {};
      ro.disconnect();
      io.disconnect();
      removeEventListener('pointermove', onMove);
      sky?.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="sk" id="top" ref={sec} aria-labelledby="sk-title">
      <div className={`sk-stage gl-${gl}`} ref={stage}>
        <div className="sk-pan" aria-hidden>
          <div className="sk-sky">
            <i className="stars" />
            <i className="sun" />
          </div>
          <div className="sk-ground" />
        </div>
        <div className="sk-fallback" aria-hidden>
          {Array.from({ length: 9 }, (_, i) => (
            <i key={i} />
          ))}
        </div>
        <canvas className="sk-c" ref={backC} aria-hidden />
        <div className="sk-kites" ref={kites} aria-hidden>
          <Kite hue="#ff6f61" className="k1" />
          <Kite hue="#ffc24a" className="k2" />
        </div>
        <div className="sk-center">
          <div className="sk-mark" ref={mark}>
            <h1 className="sk-word" id="sk-title" aria-label="GG Bharat">
              <span className="sk-w1" aria-hidden>
                GG
              </span>
              <span className="sk-wheel" aria-hidden>
                <Wheel />
              </span>
              <span className="sk-w2" aria-hidden>
                BHARAT
              </span>
            </h1>
            <Scripts />
          </div>
        </div>
        <canvas className="sk-c front" ref={frontC} aria-hidden />
        <div className="sk-copy" ref={copy}>
          <p className="sk-tag">
            India’s worlds, <em>built to play.</em>
          </p>
          <p className="sk-sub">Real Indian cities you can walk into, live shows and events, in 3D, in your browser. First stop: Chandigarh, the City Beautiful.</p>
          <div className="sk-acts">
            <button type="button" className="gx-btn lg" onClick={() => openRegister()}>
              Join the waitlist · ₹{REG.fee}
            </button>
            <button type="button" className="gx-btn ghost lg" onClick={onDescend}>
              See Chandigarh <span aria-hidden>↓</span>
            </button>
          </div>
          <p className="sk-beta mono">
            <i /> Waitlist open · founders get in first
          </p>
        </div>
        <div className="sk-hint mono" aria-hidden>
          <span>Scroll to descend</span>
          <i />
        </div>
        <div className="sk-veil" aria-hidden />
      </div>
    </section>
  );
}
